import React, { useState, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ShareNetwork, Copy, Check, X, DownloadSimple, FloppyDisk,
  TwitterLogo, LinkedinLogo, WhatsappLogo,
  FacebookLogo, TelegramLogo, EnvelopeSimple, Link as LinkIcon,
  InstagramLogo, Export
} from '@phosphor-icons/react';

const MARKETING_TAGLINE = "Created with ScalitOS — AI marketing OS for founders. Try it free";
const MARKETING_URL = "https://scalitos.com";

export function ShareResultButton({ title, content, type = 'Result', imageUrl, videoUrl, className = '', variant = 'full' }) {
  const [showModal, setShowModal] = useState(false);
  const [copied, setCopied] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const shareText = [
    title ? `${title}` : '',
    typeof content === 'string' ? content.slice(0, 400) : '',
    '',
    MARKETING_TAGLINE,
    MARKETING_URL,
  ].filter(Boolean).join('\n');

  // Native Web Share API
  const canNativeShare = typeof navigator !== 'undefined' && !!navigator.share;

  const handleNativeShare = async () => {
    try {
      const shareData = {
        title: title || `My ${type} — ScalitOS`,
        text: shareText.slice(0, 300),
        url: MARKETING_URL,
      };
      await navigator.share(shareData);
    } catch (err) {
      // User cancelled or not supported — fall back to modal
      if (err.name !== 'AbortError') setShowModal(true);
    }
  };

  const handleShareClick = () => {
    if (canNativeShare) {
      handleNativeShare();
    } else {
      setShowModal(true);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(shareText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const copyLink = () => {
    navigator.clipboard.writeText(MARKETING_URL);
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  };

  const shareToTwitter = () => {
    const text = encodeURIComponent(`${title ? title + ' — ' : ''}${MARKETING_TAGLINE}\n\n${MARKETING_URL}`);
    window.open(`https://twitter.com/intent/tweet?text=${text}`, '_blank');
  };

  const shareToLinkedIn = () => {
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(MARKETING_URL)}`, '_blank');
  };

  const shareToWhatsApp = () => {
    window.open(`https://wa.me/?text=${encodeURIComponent(shareText.slice(0, 500))}`, '_blank');
  };

  const shareToFacebook = () => {
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(MARKETING_URL)}&quote=${encodeURIComponent(title || MARKETING_TAGLINE)}`, '_blank');
  };

  const shareToTelegram = () => {
    window.open(`https://t.me/share/url?url=${encodeURIComponent(MARKETING_URL)}&text=${encodeURIComponent(shareText.slice(0, 500))}`, '_blank');
  };

  const shareToInstagram = () => {
    // Instagram doesn't support direct web sharing — copy content and open app
    navigator.clipboard.writeText(shareText.slice(0, 500));
    window.open('https://www.instagram.com/', '_blank');
  };

  const shareViaEmail = () => {
    const subject = encodeURIComponent(title || `Check out my ${type} from ScalitOS`);
    const body = encodeURIComponent(shareText);
    window.open(`mailto:?subject=${subject}&body=${body}`, '_self');
  };

  const downloadShareCard = useCallback(async () => {
    setDownloading(true);
    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      canvas.width = 1080;
      canvas.height = imageUrl ? 1350 : 1080;

      ctx.fillStyle = '#080808';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      let yOffset = 80;

      if (imageUrl) {
        try {
          const img = new window.Image();
          img.crossOrigin = 'anonymous';
          await new Promise((resolve, reject) => {
            img.onload = resolve;
            img.onerror = reject;
            img.src = imageUrl;
          });
          const imgSize = 600;
          const imgX = (canvas.width - imgSize) / 2;
          ctx.save();
          ctx.beginPath();
          ctx.roundRect(imgX, yOffset, imgSize, imgSize, 24);
          ctx.clip();
          ctx.drawImage(img, imgX, yOffset, imgSize, imgSize);
          ctx.restore();
          ctx.strokeStyle = 'rgba(255,255,255,0.1)';
          ctx.lineWidth = 2;
          ctx.beginPath();
          ctx.roundRect(imgX, yOffset, imgSize, imgSize, 24);
          ctx.stroke();
          yOffset += imgSize + 40;
        } catch {}
      }

      ctx.fillStyle = '#c8f060';
      ctx.fillRect(60, yOffset, 4, 40);
      ctx.font = '600 16px sans-serif';
      ctx.fillText(type.toUpperCase(), 80, yOffset + 26);
      yOffset += 60;

      if (title) {
        ctx.fillStyle = '#f5f0e8';
        ctx.font = '600 36px serif';
        wrapText(ctx, title, 60, yOffset, 960, 44);
        yOffset += 60;
      }

      if (content) {
        ctx.fillStyle = 'rgba(245,240,232,0.5)';
        ctx.font = '20px sans-serif';
        const contentStr = typeof content === 'string' ? content : JSON.stringify(content);
        wrapText(ctx, contentStr.slice(0, 300), 60, yOffset, 960, 28);
      }

      const footerY = canvas.height - 70;
      ctx.fillStyle = 'rgba(255,255,255,0.04)';
      ctx.fillRect(0, footerY, canvas.width, 70);
      ctx.fillStyle = '#c8f060';
      ctx.font = '600 14px sans-serif';
      ctx.fillText('ScalitOS', 60, footerY + 38);
      ctx.fillStyle = 'rgba(245,240,232,0.25)';
      ctx.font = '13px sans-serif';
      ctx.fillText('Your Marketing Team  •  scalitos.com', 160, footerY + 38);

      const link = document.createElement('a');
      link.download = `scalitos-${type.toLowerCase().replace(/\s+/g, '-')}-${Date.now()}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } finally {
      setDownloading(false);
    }
  }, [title, content, type, imageUrl]);

  const downloadMediaDirect = useCallback(async () => {
    setDownloading(true);
    try {
      const mediaUrl = videoUrl || imageUrl;
      if (mediaUrl) {
        // Try native share for mobile (save to photos)
        if (navigator.share && navigator.canShare) {
          try {
            const response = await fetch(mediaUrl);
            const blob = await response.blob();
            const ext = videoUrl ? 'mp4' : 'png';
            const mimeType = videoUrl ? 'video/mp4' : 'image/png';
            const file = new File([blob], `scalitos-${type.toLowerCase().replace(/\s+/g, '-')}.${ext}`, { type: mimeType });
            if (navigator.canShare({ files: [file] })) {
              await navigator.share({ files: [file], title: title || `ScalitOS ${type}` });
              setDownloading(false);
              return;
            }
          } catch (err) {
            if (err.name === 'AbortError') { setDownloading(false); return; }
          }
        }
        // Fallback: download
        const link = document.createElement('a');
        link.href = mediaUrl;
        link.download = `scalitos-${type.toLowerCase().replace(/\s+/g, '-')}-${Date.now()}.${videoUrl ? 'mp4' : 'png'}`;
        link.click();
      } else {
        // No direct media — download a share card instead
        await downloadShareCard();
        setDownloading(false);
        return;
      }
    } catch {
      // Fall back to share card
      await downloadShareCard();
    }
    setDownloading(false);
  }, [title, content, type, imageUrl, videoUrl, downloadShareCard]);

  const SHARE_OPTIONS = [
    { icon: FloppyDisk, label: 'Save to Device', onClick: downloadMediaDirect, testId: 'share-save-device', highlight: true },
    { icon: copied ? Check : Copy, label: copied ? 'Copied!' : 'Copy Text', onClick: copyToClipboard, active: copied, testId: 'share-copy' },
    { icon: downloading ? null : DownloadSimple, label: downloading ? 'Creating...' : 'Download Card', onClick: downloadShareCard, testId: 'share-download' },
    { icon: TwitterLogo, label: 'Twitter / X', onClick: shareToTwitter, testId: 'share-twitter' },
    { icon: LinkedinLogo, label: 'LinkedIn', onClick: shareToLinkedIn, testId: 'share-linkedin' },
    { icon: WhatsappLogo, label: 'WhatsApp', onClick: shareToWhatsApp, testId: 'share-whatsapp' },
    { icon: FacebookLogo, label: 'Facebook', onClick: shareToFacebook, testId: 'share-facebook' },
    { icon: InstagramLogo, label: 'Instagram', onClick: shareToInstagram, testId: 'share-instagram' },
    { icon: TelegramLogo, label: 'Telegram', onClick: shareToTelegram, testId: 'share-telegram' },
    { icon: EnvelopeSimple, label: 'Email', onClick: shareViaEmail, testId: 'share-email' },
    { icon: linkCopied ? Check : LinkIcon, label: linkCopied ? 'Link Copied!' : 'Copy Link', onClick: copyLink, active: linkCopied, testId: 'share-copy-link' },
  ];

  return (
    <>
      <button
        onClick={handleShareClick}
        data-testid={`share-result-btn-${type.toLowerCase().replace(/\s+/g, '-')}`}
        className={`flex items-center gap-1.5 rounded-lg text-xs font-medium transition-all hover:scale-105 ${className}`}
        style={{
          background: 'linear-gradient(135deg, rgba(200,240,96,0.1), rgba(200,240,96,0.05))',
          color: '#c8f060',
          border: '1px solid rgba(200,240,96,0.15)',
          padding: variant === 'icon' ? '6px' : '6px 12px',
        }}
      >
        {canNativeShare ? <Export size={14} weight="bold" /> : <ShareNetwork size={14} weight="bold" />}
        {variant !== 'icon' && (canNativeShare ? 'Share' : 'Share Result')}
      </button>

      {/* Desktop fallback modal */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[150] flex items-center justify-center"
            style={{ background: 'rgba(0,0,0,0.8)', backdropFilter: 'blur(12px)' }}
            onClick={() => setShowModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              className="w-full max-w-md mx-4 rounded-2xl overflow-hidden max-h-[90vh] overflow-y-auto"
              style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.08)' }}
              onClick={e => e.stopPropagation()}
              data-testid="share-result-modal"
            >
              <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: 'rgba(255,255,255,0.07)' }}>
                <div className="flex items-center gap-2">
                  <ShareNetwork size={18} className="text-[#c8f060]" />
                  <h3 className="font-medium" style={{ color: '#f5f0e8', fontFamily: "'Instrument Serif', serif" }}>Share Everywhere</h3>
                </div>
                <button onClick={() => setShowModal(false)} className="text-zinc-500 hover:text-white transition-colors">
                  <X size={18} />
                </button>
              </div>

              <div className="p-6 space-y-4">
                {/* Preview card */}
                <div className="rounded-xl overflow-hidden" style={{ background: '#080808', border: '1px solid rgba(255,255,255,0.06)' }}>
                  {imageUrl && (
                    <div className="flex items-center justify-center p-4 pb-0">
                      <img src={imageUrl} alt={title || type} className="max-h-48 rounded-lg object-contain" />
                    </div>
                  )}
                  <div className="p-4">
                    <span className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: '#c8f060' }}>{type}</span>
                    {title && <p className="text-sm font-medium mt-1 line-clamp-2" style={{ fontFamily: "'Instrument Serif', serif", color: '#f5f0e8' }}>{title}</p>}
                    {content && (
                      <p className="text-xs mt-1 line-clamp-3" style={{ color: 'rgba(245,240,232,0.4)' }}>
                        {typeof content === 'string' ? content : JSON.stringify(content).slice(0, 200)}
                      </p>
                    )}
                  </div>
                </div>

                {/* Share grid */}
                <div className="grid grid-cols-2 gap-2">
                  {SHARE_OPTIONS.map(opt => (
                    <button
                      key={opt.testId}
                      onClick={opt.onClick}
                      data-testid={opt.testId}
                      className={`flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-medium transition-all hover:bg-white/10 ${opt.testId === 'share-save-device' ? 'col-span-2' : ''} ${opt.testId === 'share-copy-link' ? 'col-span-2' : ''}`}
                      style={{
                        background: opt.highlight ? '#c8f060' : opt.active ? 'rgba(200,240,96,0.1)' : 'rgba(255,255,255,0.04)',
                        color: opt.highlight ? '#080808' : opt.active ? '#c8f060' : 'rgba(245,240,232,0.55)',
                        border: opt.highlight ? 'none' : `1px solid ${opt.active ? 'rgba(200,240,96,0.2)' : 'rgba(255,255,255,0.06)'}`,
                        fontWeight: opt.highlight ? '600' : '500',
                      }}
                    >
                      {opt.icon && <opt.icon size={14} />}
                      {opt.label}
                    </button>
                  ))}
                </div>

                <p className="text-center text-[10px]" style={{ color: 'rgba(245,240,232,0.15)' }}>
                  Every share includes a short ScalitOS promo — help us grow!
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

function wrapText(ctx, text, x, y, maxWidth, lineHeight) {
  const words = text.split(' ');
  let line = '';
  let lines = 0;
  for (let i = 0; i < words.length; i++) {
    const testLine = line + words[i] + ' ';
    const metrics = ctx.measureText(testLine);
    if (metrics.width > maxWidth && i > 0) {
      ctx.fillText(line, x, y);
      line = words[i] + ' ';
      y += lineHeight;
      lines++;
      if (lines > 15) break;
    } else {
      line = testLine;
    }
  }
  ctx.fillText(line, x, y);
  return lines + 1;
}
